import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config';
import { usersRef } from './read';
import { UserCredential } from 'firebase/auth';
import { registerStatus, userRole } from '../../../../constants/enum';
import { RegisterStaffType } from '../../../../components/uiParts/card/StaffRegisterCard';
import { uploadFile } from '../../storage';

/**
 * 担当者招待からの登録を完了する
 *
 * users の仮ドキュメント操作（検索・削除・新規作成）は admin SDK が必要なため
 * completeRegistration Function に委譲する。
 */
export const addStaffUser = async (
  email: string | null,
  createdUser: UserCredential,
  watch: <K extends keyof RegisterStaffType>(key: K) => RegisterStaffType[K],
  fileIcon: FileList | null,
): Promise<void> => {
  const cleanEmail = email ? email.split('?').join('') : '';

  // users の仮ドキュメント操作・staffInvitations 更新を Functions 経由で実行
  const completeRegistration = httpsCallable<
    // 部署名は任意入力のため未入力になり得る（#3071）
    { type: 'staff'; email: string; uid: string; name: string; department?: string },
    { success: boolean; uid: string; companyId: string; role: number }
  >(functions, 'completeRegistration');

  await completeRegistration({
    type: 'staff',
    email: cleanEmail,
    uid: createdUser.user.uid,
    name: watch('name') as string,
    department: (watch('department') as string) || '',
  });

  // アイコンのアップロード（Storage操作はFunctionsに含められないため、バッチ外で実行）
  const newUid = createdUser.user.uid;
  const fileIconName = fileIcon !== null ? fileIcon[0].name : '';
  const icon = fileIcon !== null ? `users/${newUid}/profile/${Date.now()}_${fileIconName}` : null;

  if (icon !== null) {
    const { storagePath, iconUrl } = await uploadFile(fileIcon, icon);
    const userUpdateData = {
      icon: storagePath,
      icon_url: iconUrl,
      updated_at: new Date(),
    };
    await updateDoc(doc(db, 'users', newUid), userUpdateData);
  }
};

/**
 * メール送信時に匿名ユーザーを作成する
 */
export const createStaffUserWhenSendemail = async (
  email: string,
  watch: (arg0: string) => string | boolean | null,
): Promise<void> => {
  const newDocRef = doc(usersRef);

  const data = {
    uid: newDocRef.id,
    created_at: new Date(),
    email,
    invite_flg: true,
    register_status: registerStatus.emailRegistered,
    role:
      (watch('role') as string | boolean | null) === 'true'
        ? userRole.companyAdmin
        : (watch('role') as string | boolean | null) === 'false'
          ? userRole.companyMember
          : (watch('role') as string | boolean | null)
            ? userRole.companyAdmin
            : userRole.companyMember,
    updated_at: new Date(),
  };

  // ドキュメントを作成
  await setDoc(newDocRef, data);
};
